// Lightweight contenteditable rich text editor.
//
// Powers any element wrapped with [data-rich-text] containing:
//   - [data-rich-editor]   contenteditable div (the writing surface)
//   - [data-rich-input]    hidden input (the form-submit value)
//   - [data-rich-cmd="..."] toolbar buttons (bold, italic, lists)
//   - [data-rich-link]      link toolbar button (opens inline popover)
//   - [data-rich-link-popover] (optional) inline popover for link entry
//
// Contract:
//   - Input is HTML (whatever `data-rich-input value=""` carries).
//   - Output is HTML stored in the same hidden input on every edit.
//   - Pasted content is sanitised to an allow-list to keep external
//     styling (Word, Google Docs, etc.) from leaking in.
//
// Server-side sanitisation is the caller's responsibility — store
// the HTML through a sanitiser (e.g. bluemonday's UGCPolicy) before
// rendering it back via templ.Raw. This editor blocks the worst
// shapes at paste time but trusts the server boundary as the final
// gate.

import { safeLinkAddress as sanitiseURL } from "./link_address.js";
import { showToast } from "./toast.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

// Tags retained when sanitising pasted HTML. Anything outside this
// list is unwrapped (children kept, parent dropped). Inline styles,
// classes, ids, and event handlers are always stripped.
const ALLOWED_TAGS = new Set([
  "P",
  "BR",
  "STRONG",
  "B",
  "EM",
  "I",
  "U",
  "UL",
  "OL",
  "LI",
  "A",
  "BLOCKQUOTE",
  "H1",
  "H2",
  "H3",
  "H4",
  "IMG",
]);

// Attribute allow-list per tag. Anything else is stripped during
// sanitisation. Keep this small — every entry is something we'd
// have to defend against (CSS injection via style="", JS via
// onerror="", etc.).
const ALLOWED_ATTRS = {
  A: new Set(["href", "title"]),
  IMG: new Set(["src", "alt"]),
};

// internalImageSrc returns src only when it is the relative
// image-serve route this app embeds (/academy/courses/<id>/images/<id>);
// anything else (external, data:, tracking pixels) becomes "" so the
// caller drops the image. Mirrors richtext.SanitizeWithImages on the
// server, which is the authoritative gate.
const ACADEMY_IMAGE_SRC =
  /^\/academy\/courses\/[0-9a-fA-F-]{36}\/images\/[0-9a-fA-F-]{36}$/;

function internalImageSrc(raw) {
  if (!raw) return "";
  const trimmed = raw.trim();
  return ACADEMY_IMAGE_SRC.test(trimmed) ? trimmed : "";
}

export function initRichText(root = document) {
  const wrappers = root.querySelectorAll("[data-rich-text]");
  wrappers.forEach((wrapper) => {
    if (!firstBind(wrapper)) return;
    wireEditor(wrapper);
  });
}

function wireEditor(wrapper) {
  const editor = wrapper.querySelector("[data-rich-editor]");
  const input = wrapper.querySelector("[data-rich-input]");
  if (!editor || !input) return;

  const sync = () => {
    input.value = editor.innerHTML;
  };

  editor.addEventListener("input", sync);
  editor.addEventListener("blur", sync);

  wrapper.querySelectorAll("[data-rich-cmd]").forEach((button) => {
    // Keep selection inside the editor when clicking the toolbar.
    button.addEventListener("mousedown", (event) => event.preventDefault());
    button.addEventListener("click", () => {
      editor.focus();
      runCommand(button.dataset.richCmd);
      sync();
    });
  });

  wireLinkPopover(wrapper, editor, sync);
  wireImageButton(wrapper, editor, sync);
  wirePasteHandler(editor, sync);

  // Reflect the initial server-rendered value into the hidden input
  // so a form submission without any keystrokes still carries it.
  sync();
}

// runCommand wraps document.execCommand with the small set of
// commands we expose. execCommand is deprecated but still the most
// reliable cross-browser primitive for the toolbar buttons we ship;
// the modern alternatives (Selection / Range manipulation) are
// hundreds of lines per operation. We isolate the call here so a
// future swap to a maintained library is one function to replace.
function runCommand(command) {
  if (!command) return;
  // execCommand returns false on unrecognised commands; nothing to
  // do — the toolbar buttons only fire whitelisted commands.
  // eslint-disable-next-line no-restricted-syntax
  document.execCommand(command, false, null);
}

// wireLinkPopover replaces the legacy `window.prompt("Enter URL")`
// link entry with an inline popover. If the wrapper doesn't carry
// the popover markup we fall back to prompt so older callers stay
// functional during the rollout.
function wireLinkPopover(wrapper, editor, sync) {
  const button = wrapper.querySelector("[data-rich-link]");
  if (!button) return;

  const popover = wrapper.querySelector("[data-rich-link-popover]");
  const input = wrapper.querySelector("[data-rich-link-input]");
  const apply = wrapper.querySelector("[data-rich-link-apply]");
  const cancel = wrapper.querySelector("[data-rich-link-cancel]");

  button.addEventListener("mousedown", (event) => event.preventDefault());

  if (!popover || !input || !apply) {
    // Legacy fallback.
    button.addEventListener("click", () => {
      const url = window.prompt("Enter URL");
      if (!url) return;
      const cleaned = sanitiseURL(url);
      if (!cleaned) return;
      editor.focus();
      // eslint-disable-next-line no-restricted-syntax
      document.execCommand("createLink", false, cleaned);
      sync();
    });
    return;
  }

  let savedRange = null;

  const closePopover = () => {
    popover.classList.add("hidden");
    input.value = "";
    savedRange = null;
  };

  button.addEventListener("click", () => {
    // Capture the current selection so we can restore it after the
    // user types into the popover input (typing into the input
    // collapses the editor's own selection).
    const sel = window.getSelection();
    savedRange =
      sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    popover.classList.remove("hidden");
    input.focus();
  });

  const apply_ = () => {
    const raw = input.value.trim();
    const cleaned = sanitiseURL(raw);
    if (!cleaned) {
      input.focus();
      return;
    }
    editor.focus();
    if (savedRange) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange);
    }
    // eslint-disable-next-line no-restricted-syntax
    document.execCommand("createLink", false, cleaned);
    sync();
    closePopover();
  };

  apply.addEventListener("mousedown", (event) => event.preventDefault());
  apply.addEventListener("click", apply_);
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      apply_();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closePopover();
    }
  });

  if (cancel) {
    cancel.addEventListener("mousedown", (event) => event.preventDefault());
    cancel.addEventListener("click", closePopover);
  }
}

// wirePasteHandler intercepts paste events, walks the pasted HTML,
// strips disallowed tags + attributes, and inserts the cleaned
// fragment. Mitigates the largest UX bug in the legacy component:
// pasting from Word or Google Docs would carry every style attribute
// + class + custom xmlns the source had, mangling the editor.
function wirePasteHandler(editor, sync) {
  editor.addEventListener("paste", (event) => {
    event.preventDefault();
    const clipboard = event.clipboardData;
    if (!clipboard) return;

    const html = clipboard.getData("text/html");
    const text = clipboard.getData("text/plain");

    let fragment;
    if (html) {
      fragment = sanitiseHTML(html);
    } else if (text) {
      fragment = document.createDocumentFragment();
      // Preserve line breaks as paragraphs / <br>, mirroring how a
      // browser normally splits plain-text paste.
      text.split(/\r?\n/).forEach((line, idx, lines) => {
        fragment.appendChild(document.createTextNode(line));
        if (idx < lines.length - 1) {
          fragment.appendChild(document.createElement("br"));
        }
      });
    }
    if (!fragment) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      editor.appendChild(fragment);
    } else {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(fragment);
      range.collapse(false);
    }
    sync();
  });
}

// sanitiseHTML parses the supplied HTML into a DOM fragment and
// removes anything outside ALLOWED_TAGS / ALLOWED_ATTRS. Returns a
// DocumentFragment safe to insert into the editor.
function sanitiseHTML(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return document.createDocumentFragment();
  sanitiseNode(root);

  const fragment = document.createDocumentFragment();
  Array.from(root.childNodes).forEach((node) => {
    fragment.appendChild(node);
  });
  return fragment;
}

// sanitiseNode mutates the tree in place. Nodes outside the allow-
// list are replaced by their children; attributes outside the per-
// tag allow-list are stripped. Anchor hrefs are run through the URL
// sanitiser to block javascript: / data: schemes.
function sanitiseNode(node) {
  // Walk children first so we operate on a stable tree shape — we
  // unwrap as we go without disturbing siblings yet to be visited.
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      sanitiseNode(child);
    } else if (child.nodeType === Node.COMMENT_NODE) {
      child.remove();
    }
  });

  if (node === node.ownerDocument.body.firstElementChild) return;
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  if (!ALLOWED_TAGS.has(node.tagName)) {
    unwrap(node);
    return;
  }

  const allowedAttrs = ALLOWED_ATTRS[node.tagName] || new Set();
  Array.from(node.attributes).forEach((attr) => {
    if (!allowedAttrs.has(attr.name.toLowerCase())) {
      node.removeAttribute(attr.name);
    }
  });

  if (node.tagName === "A") {
    const href = node.getAttribute("href");
    const cleaned = href ? sanitiseURL(href) : "";
    if (!cleaned) {
      unwrap(node);
      return;
    }
    node.setAttribute("href", cleaned);
    node.setAttribute("rel", "noopener noreferrer");
    node.setAttribute("target", "_blank");
  }

  if (node.tagName === "IMG") {
    // Only this app's own embedded screenshots survive a paste; an
    // <img> with any other src is dropped entirely (it has no text
    // children to keep).
    const cleaned = internalImageSrc(node.getAttribute("src"));
    if (!cleaned) {
      node.remove();
      return;
    }
    node.setAttribute("src", cleaned);
  }
}

function unwrap(node) {
  const parent = node.parentNode;
  if (!parent) return;
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
}

// wireImageButton wires the inline-screenshot toolbar button. Present
// only when the editor opted in via data-rich-image-upload (a course's
// image base path). The flow mirrors the document-attachment uploader:
// sign → direct PUT → commit, then embed the returned serve-route src
// as an <img> at the cursor.
function wireImageButton(wrapper, editor, sync) {
  const basePath = wrapper.dataset.richImageUpload;
  const button = wrapper.querySelector("[data-rich-image-btn]");
  const fileInput = wrapper.querySelector("[data-rich-image-input]");
  if (!basePath || !button || !fileInput) return;

  let savedRange = null;

  button.addEventListener("mousedown", (event) => event.preventDefault());
  button.addEventListener("click", () => {
    // Capture the caret so the image lands where the author was typing,
    // not at the end of the editor.
    const sel = window.getSelection();
    savedRange =
      sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)
        ? sel.getRangeAt(0).cloneRange()
        : null;
    fileInput.click();
  });

  fileInput.addEventListener("change", async () => {
    const file = fileInput.files && fileInput.files[0];
    // Reset so picking the same file twice still fires change.
    fileInput.value = "";
    if (!file) return;

    button.disabled = true;
    try {
      const src = await uploadCourseImage(basePath, file);
      insertImage(editor, savedRange, src, defaultAlt(file.name));
      sync();
    } catch (err) {
      console.error("rich-text image upload failed", err);
      showToast(
        err && err.message
          ? err.message
          : "Could not upload the image. Please try again.",
        "error",
      );
    } finally {
      button.disabled = false;
    }
  });
}

// uploadCourseImage runs the sign → PUT → commit round-trip and returns
// the stable serve-route src the editor embeds.
async function uploadCourseImage(basePath, file) {
  const signed = await postForm(`${basePath}/sign`, {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    file_size_bytes: String(file.size),
  });

  const putRes = await fetch(signed.upload_url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...(signed.upload_headers || {}),
    },
    body: file,
  });
  if (!putRes.ok) throw new Error("Upload failed. Please try again.");

  const committed = await postForm(basePath, { storage_key: signed.key });

  const src = internalImageSrc(committed.src);
  if (!src) throw new Error("Could not embed the image. Please try again.");

  return src;
}

// postForm POSTs a urlencoded body and returns the parsed JSON, surfacing
// the server's error message when the request fails.
async function postForm(endpoint, fields) {
  const body = new URLSearchParams();
  Object.entries(fields).forEach(([k, v]) => body.set(k, v));

  const res = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((payload && payload.error) || "Request failed.");
  }
  return payload || {};
}

// insertImage drops an <img> at the saved caret (or the editor's end if
// the selection was lost), then moves the caret past it so typing
// continues after the screenshot.
function insertImage(editor, savedRange, src, alt) {
  const img = document.createElement("img");
  img.setAttribute("src", src);
  img.setAttribute("alt", alt);

  editor.focus();
  let range = savedRange;
  if (!range || !editor.contains(range.commonAncestorContainer)) {
    range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
  }
  range.deleteContents();
  range.insertNode(img);
  range.setStartAfter(img);
  range.collapse(true);

  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

function defaultAlt(filename) {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(0, dot) : filename;
}
